import { useUpdateCommunity } from '@communities/community/hooks';
import { addSecurityGroup, updateSecurityGroup } from '@communities/community/utils/securityGroupsUtils';
import { useUpdateCommunityGroup } from '@communities/community-groups/hooks';
import type { EntityType, SecurityGroups } from '@communities/content-management/domain';
import { isCommunity } from '@communities/content-management/utils';
import { ErrorBanner } from '@core/components';
import { Select, TextInput, LoadingSpinner, Dialog } from '@soteria/design-system-ve';
import { useState, useEffect } from 'react';

interface SecurityGroupItem {
  id: string;
  groupName: string;
  role: 'admin' | 'moderator' | 'user';
}

interface SecurityGroupDialogProps {
  isOpen: boolean;
  onClose: () => void;
  editingGroup?: SecurityGroupItem | null;
  existingGroups: SecurityGroupItem[];
  entityId: string;
  entityType: EntityType;
  securityGroups?: SecurityGroups; // Pass security groups from parent to avoid hook issues
}

/**
 * Dialog component for adding or editing security groups
 */
// eslint-disable-next-line complexity
export function SecurityGroupDialog({ isOpen, onClose, editingGroup, entityId, entityType, securityGroups: passedSecurityGroups }: SecurityGroupDialogProps) {
  const [groupName, setGroupName] = useState('');
  const [role, setRole] = useState<'admin' | 'moderator' | 'user'>('user');
  const [isLoading] = useState(false);

  // Get update hooks only
  const communityHook = useUpdateCommunity();
  const communityGroupHook = useUpdateCommunityGroup();

  // Get the appropriate properties from the hooks
  const communityHookData = isCommunity(entityType) ? communityHook : { updateCommunity: undefined, isUpdating: false, errors: [], resetErrors: () => {} };
  const communityGroupHookData = !isCommunity(entityType)
    ? communityGroupHook
    : { updateCommunityGroup: undefined, loading: false, errors: [], resetErrors: () => {} };

  const loading = isCommunity(entityType) ? communityHookData.isUpdating : communityGroupHookData.loading;
  const errors = isCommunity(entityType) ? communityHookData.errors : communityGroupHookData.errors;
  const resetErrors = isCommunity(entityType) ? communityHookData.resetErrors : communityGroupHookData.resetErrors;

  // Use passed security groups or fallback to empty structure
  const securityGroups = passedSecurityGroups || { admin: [], moderator: [], user: [] };

  const isEditing = Boolean(editingGroup);

  // Helper function to update the appropriate entity based on type
  const updateEntity = async (id: string, input: { securityGroups: SecurityGroups }): Promise<boolean> => {
    if (isCommunity(entityType) && communityHookData.updateCommunity) {
      return communityHookData.updateCommunity(id, input);
    } else if (!isCommunity(entityType) && communityGroupHookData.updateCommunityGroup) {
      return communityGroupHookData.updateCommunityGroup(id, input);
    }

    return false;
  };

  // Reset form when dialog opens/closes or editing group changes
  useEffect(() => {
    if (isOpen) {
      if (editingGroup) {
        setGroupName(editingGroup.groupName);
        setRole(editingGroup.role);
      } else {
        setGroupName('');
        setRole('user');
      }

      resetErrors();
    }
  }, [isOpen, editingGroup, resetErrors]);

  const handleSave = async () => {
    resetErrors();

    let updatedSecurityGroups;

    if (editingGroup) {
      // Update existing security group
      updatedSecurityGroups = updateSecurityGroup(securityGroups, {
        oldGroupName: editingGroup.groupName,
        oldRole: editingGroup.role,
        newGroupName: groupName.trim(),
        newRole: role,
      });
    } else {
      // Add new security group
      updatedSecurityGroups = addSecurityGroup(securityGroups, groupName.trim(), role);
    }

    // Remove __typename from the security groups object to avoid GraphQL input validation errors
    const cleanSecurityGroups = {
      admin: updatedSecurityGroups.admin || [],
      moderator: updatedSecurityGroups.moderator || [],
      user: updatedSecurityGroups.user || [],
    };

    const success = await updateEntity(entityId, {
      securityGroups: cleanSecurityGroups,
    });

    if (success) {
      onClose();
    }
  };

  const roleOptions = [
    { value: 'admin', label: 'Admin' },
    { value: 'moderator', label: 'Moderator' },
    { value: 'user', label: 'User' },
  ];

  const isFormValid = groupName.trim() !== '';
  const totalLoading = isLoading || loading;

  return (
    <Dialog
      isOpen={isOpen}
      primaryAction={{
        label: totalLoading ? <LoadingSpinner /> : isEditing ? 'UPDATE SECURITY GROUP' : 'ADD SECURITY GROUP',
        onClick: handleSave,
        disabled: totalLoading || !isFormValid,
      }}
      size="md"
      title={isEditing ? 'Edit Security Group' : 'Add Security Group'}
      onClose={onClose}
    >
      {/* Error Banner */}
      {errors && errors.length > 0 && <ErrorBanner errors={errors} title="There was an error saving the security group:" />}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <TextInput
          required
          disabled={totalLoading}
          helperText="Enter the exact name of the security group from your identity provider"
          label="Security Group Name *"
          placeholder="Enter security group name (e.g., MyOrg-Admins)"
          value={groupName}
          onChange={setGroupName}
        />
        <Select
          disabled={totalLoading}
          helperText="Select the role that members of this security group should have"
          label="Role"
          options={roleOptions}
          value={role}
          onChange={value => setRole(value as 'admin' | 'moderator' | 'user')}
        />
      </div>
    </Dialog>
  );
}
